import React, { useEffect, useRef, useState } from 'react';

import { Alert, Divider, Empty, Spin, Typography } from 'antd';

import { api } from '../../http/API';
import { monitorAPI } from '../../http/MonitorAPI';
import { Field } from '../../models/Field';
import { FieldCropHistory } from '../../models/FieldCropHistory';
import { FieldReport } from '../../models/FieldReport';

import ChartsBlock from './components/ChartsBlock';
import DailyDetails from './components/DailyDetails';
import EventsList from './components/EventsList';
import PhaseTimeline from './components/PhaseTimeline';
import PrevYearCompare from './components/PrevYearCompare';
import Recommendations from './components/Recommendations';
import ReportHeader from './components/ReportHeader';
import SummaryTiles from './components/SummaryTiles';

const { Title, Text } = Typography;

const ReportsPage = () => {
    const [fields, setFields] = useState<Field[]>([]);
    const [fieldsLoading, setFieldsLoading] = useState<boolean>(false);

    const [seasons, setSeasons] = useState<FieldCropHistory[]>([]);
    const [seasonsLoading, setSeasonsLoading] = useState<boolean>(false);

    const [selectedFieldId, setSelectedFieldId] = useState<number | undefined>(() => {
        const saved = sessionStorage.getItem('report_field_id');
        return saved ? Number(saved) : undefined;
    });
    const [selectedSeason, setSelectedSeason] = useState<number | undefined>(() => {
        const saved = sessionStorage.getItem('report_season');
        return saved ? Number(saved) : undefined;
    });

    const [report, setReport] = useState<FieldReport | null>(() => {
        const saved = sessionStorage.getItem('report_data');
        try {
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });
    const [loaderOn, setLoaderOn] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>('');

    // Загрузка полей
    useEffect(() => {
        setFieldsLoading(true);
        api.GetFields()
            .then((data) => setFields(data || []))
            .catch(() => setFields([]))
            .finally(() => setFieldsLoading(false));
    }, []);

    const prevFieldIdRef = useRef<number | undefined>(selectedFieldId);

    // Загрузка сезонов при смене поля
    useEffect(() => {
        if (!selectedFieldId) {
            setSeasons([]);
            setSelectedSeason(undefined);
            setReport(null);
            setErrorMsg('');
            prevFieldIdRef.current = undefined;
            return;
        }

        const isInitialLoad = prevFieldIdRef.current === selectedFieldId;
        prevFieldIdRef.current = selectedFieldId;

        setSeasonsLoading(true);

        if (!isInitialLoad) {
            setReport(null);
            setErrorMsg('');
        }

        api.GetFieldSeasons(selectedFieldId)
            .then((data) => {
                const list = data || [];
                setSeasons(list);
                if (!isInitialLoad) {
                    setSelectedSeason(list.length > 0 ? list[0].season_harvest : undefined);
                }
            })
            .catch(() => {
                setSeasons([]);
                if (!isInitialLoad) {
                    setSelectedSeason(undefined);
                }
            })
            .finally(() => setSeasonsLoading(false));
    }, [selectedFieldId]);

    const fetchReport = () => {
        if (!selectedFieldId || !selectedSeason) return;
        setLoaderOn(true);
        setReport(null);
        setErrorMsg('');

        monitorAPI
            .GetFieldReport(selectedFieldId, selectedSeason)
            .then((data) => {
                setReport(data);
                sessionStorage.setItem('report_field_id', String(selectedFieldId));
                sessionStorage.setItem('report_season', String(selectedSeason));
                sessionStorage.setItem('report_data', JSON.stringify(data));
            })
            .catch((error: any) => {
                const msg =
                    error?.response?.data?.message ||
                    'Не удалось получить отчёт. Повторите попытку позже.';
                setErrorMsg(msg);
            })
            .finally(() => setLoaderOn(false));
    };

    return (
        <>
            <div className="reports-controls">
                <ReportHeader
                    fields={fields}
                    seasons={seasons}
                    selectedFieldId={selectedFieldId}
                    selectedSeason={selectedSeason}
                    onFieldChange={(id) => {
                        setSelectedFieldId(id);
                        setReport(null);
                        sessionStorage.removeItem('report_data');
                    }}
                    onSeasonChange={(year) => {
                        setSelectedSeason(year);
                        setReport(null);
                        sessionStorage.removeItem('report_data');
                    }}
                    onLoad={fetchReport}
                    onPrint={() => window.print()}
                    loading={loaderOn}
                    hasData={!!report}
                    fieldsLoading={fieldsLoading}
                    seasonsLoading={seasonsLoading}
                />
            </div>
            <Divider />
            {loaderOn && <Spin size="large" />}
            {!loaderOn && errorMsg && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Не удалось построить отчёт"
                    description={errorMsg}
                />
            )}
            {!loaderOn && !report && !errorMsg && (
                <Empty
                    description={
                        <span>
                            Выберите поле и сезон, затем нажмите «Построить отчёт».
                            <br />
                            <Text type="secondary">
                                Отчёт строится за вегетационный период автоматически.
                            </Text>
                        </span>
                    }
                    style={{ marginTop: 64 }}
                />
            )}
            {report && (
                <>
                    <Title level={3}>
                        {report.field.name || `Поле #${report.field.id}`}
                        {report.report.meta.culture ? ` — ${report.report.meta.culture}` : ''}
                        {report.report.meta.region ? ` (${report.report.meta.region})` : ''}
                    </Title>
                    <Text type="secondary">
                        Сезон {report.season.season_harvest}, период:{' '}
                        {report.report.meta.start_date} — {report.report.meta.end_date}
                    </Text>
                    <Divider />
                    <SummaryTiles summary={report.report.summary} />
                    <PhaseTimeline phases={report.report.phase_timeline} />
                    <ChartsBlock daily={report.report.daily} />
                    <DailyDetails daily={report.report.daily} />
                    <EventsList events={report.report.events} />
                    <Recommendations recommendations={report.report.recommendations} />
                    {report.report.prev_year && (
                        <PrevYearCompare
                            current={report.report.summary}
                            prev={report.report.prev_year}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default ReportsPage;
